import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-env browser */
/* eslint-disable no-underscore-dangle */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Graphic from '@arcgis/core/Graphic';
import LayerList from '@arcgis/core/widgets/LayerList';
import Expand from '@arcgis/core/widgets/Expand';
import BasemapToggle from '@arcgis/core/widgets/BasemapToggle';
import esriConfig from '@arcgis/core/config';
import '@arcgis/core/assets/esri/themes/light/main.css';
import entityComposition from '../../common/classes/entityComposition';
import { parseKMLToGeoJSON, findLineStringFeatures, selectLongestLineString, extractEndpoints, geojsonToArcGISPaths, getLineLength, getLineSlice, buildSegmentAttributes } from './arcGisMap/arcGisMapHelpers';
import FeaturePopover from './arcGisMap/featurePopover';
import createSegmentInteractions from './arcGisMap/segmentInteractions';
const DEFAULT_ACTIVITY_TYPE = 'Unknown';
const DEFAULT_COLOR = '#808080'; // Grey fallback
const SEGMENT_HOVER_LINE_WIDTH = 10;
const SEGMENT_HOVER_TOLERANCE = 12;
const SEGMENT_CLICK_TOLERANCE = 12;

// Get color for an activity type from legend items
const getActivityColor = function getActivityColor(activityType) {
  let items = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!activityType || items.length === 0) return DEFAULT_COLOR;

  // Find matching legend item by identifier
  const legendItem = items.find(item => {
    var _item$identifier;
    return item.identifier === activityType || ((_item$identifier = item.identifier) === null || _item$identifier === void 0 ? void 0 : _item$identifier.toLowerCase()) === (activityType === null || activityType === void 0 ? void 0 : activityType.toLowerCase());
  });
  return (legendItem === null || legendItem === void 0 ? void 0 : legendItem.color) || DEFAULT_COLOR;
};

// Helper function to extract KML URL from various file reference structures
const extractKMLUrl = kml => {
  if (!kml) return null;
  if (typeof kml === 'string') return kml;
  if (kml.remotePath) return kml.remotePath._cache || kml.remotePath._download;

  // UUID-keyed collection
  if (typeof kml === 'object' && Object.keys(kml).length > 0) {
    const fileObject = Object.values(kml)[0];
    if (fileObject !== null && fileObject !== void 0 && fileObject.remotePath) {
      return fileObject.remotePath._cache || fileObject.remotePath._download;
    }
  }
  return null;
};
const ArcGisMap = function ArcGisMap() {
  var _popoverPoint$x, _popoverPoint$y;
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$login = _ref.login,
    _ref$login2 = _ref$login === void 0 ? {} : _ref$login,
    _ref$login2$user = _ref$login2.user,
    user = _ref$login2$user === void 0 ? {} : _ref$login2$user,
    kml = _ref.kml,
    _ref$activities = _ref.activities,
    activities = _ref$activities === void 0 ? [] : _ref$activities,
    _ref$legend = _ref.legend,
    legend = _ref$legend === void 0 ? [] : _ref$legend,
    cabinetIdentifier = _ref.cabinetIdentifier;
  const mapContainerRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const mapViewRef = useRef(null);
  const centerlineLayerRef = useRef(null);
  const activityLayersRef = useRef({});
  const endpointsRef = useRef(null);
  const centerlineFeatureRef = useRef(null);
  const eventHandlesRef = useRef([]);
  const hoveredGraphicRef = useRef({
    graphic: null,
    originalSymbol: null
  });
  const popoverTargetRef = useRef(null);
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    legendContainer = _useState2[0],
    setLegendContainer = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isFullscreen = _useState4[0],
    setIsFullscreen = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedFeature = _useState6[0],
    setSelectedFeature = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    popoverPoint = _useState8[0],
    setPopoverPoint = _useState8[1];
  const _useState9 = useState({
      startPoint: null,
      endPoint: null,
      geometry: null
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    centerlineData = _useState0[0],
    setCenterlineData = _useState0[1];

  // Sync state with fullscreen changes (including ESC key exit)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const _ref2 = user || {},
      arcGisApiKey = _ref2.arcGisApiKey;
    if (arcGisApiKey) {
      esriConfig.apiKey = arcGisApiKey;
    }
    const map = new Map({
      basemap: arcGisApiKey ? 'arcgis/streets' : null
    });
    const view = new MapView({
      container: mapContainerRef.current,
      map,
      constraints: {
        minZoom: 1
      }
    });
    mapViewRef.current = view;
    let clearHoverHighlight = null;
    let disposeSegmentInteractions = null;
    let basemapToggleWidget = null;

    // Add LayerList widget for toggling layer visibility
    view.when(() => {
      if (arcGisApiKey) {
        basemapToggleWidget = new BasemapToggle({
          view,
          nextBasemap: 'arcgis/imagery'
        });
        view.ui.add(basemapToggleWidget, 'top-left');
      }
      const layerList = new LayerList({
        view
      });
      const layerListExpand = new Expand({
        view,
        content: layerList,
        expanded: false,
        expandIconClass: 'esri-icon-layers',
        expandTooltip: 'Layers'
      });
      view.ui.add(layerListExpand, 'top-right');

      // Create container for React portal-based legend
      const legendPortalContainer = document.createElement('div');
      legendPortalContainer.id = 'arcgis-legend-portal';
      const legendExpand = new Expand({
        view,
        content: legendPortalContainer,
        expanded: false,
        expandIconClass: 'esri-icon-legend',
        expandTooltip: 'Legend'
      });
      view.ui.add(legendExpand, 'bottom-right');
      setLegendContainer(legendPortalContainer);

      // Add fullscreen button (handler lives in effect to avoid exhaustive-deps)
      const toggleFullscreen = () => {
        const container = mapWrapperRef.current;
        if (!container) return;
        if (!document.fullscreenElement) {
          container.requestFullscreen().catch(err => {
            // eslint-disable-next-line no-console
            console.warn('Failed to enter fullscreen:', err);
          });
        } else {
          document.exitFullscreen();
        }
      };
      const fullscreenBtn = document.createElement('div');
      fullscreenBtn.className = 'esri-widget esri-widget--button esri-interactive';
      fullscreenBtn.setAttribute('role', 'button');
      fullscreenBtn.setAttribute('tabindex', '0');
      fullscreenBtn.setAttribute('title', 'Toggle Fullscreen');
      fullscreenBtn.setAttribute('aria-label', 'Toggle Fullscreen');
      fullscreenBtn.innerHTML = '<span class="esri-icon esri-icon-zoom-out-fixed" aria-hidden="true"></span>';
      fullscreenBtn.addEventListener('click', toggleFullscreen);
      fullscreenBtn.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleFullscreen();
        }
      });
      view.ui.add(fullscreenBtn, 'top-right');
      const _createSegmentInterac = createSegmentInteractions({
          view,
          activityLayersRef,
          hoveredGraphicRef,
          cabinetIdentifier,
          setSelectedFeature,
          setPopoverPoint,
          hoverLineWidth: SEGMENT_HOVER_LINE_WIDTH,
          hoverTolerance: SEGMENT_HOVER_TOLERANCE,
          clickTolerance: SEGMENT_CLICK_TOLERANCE
        }),
        handleMapClick = _createSegmentInterac.handleMapClick,
        handlePointerMove = _createSegmentInterac.handlePointerMove,
        clearHover = _createSegmentInterac.clearHoverHighlight,
        disposeSegmentInteractionsFromCreate = _createSegmentInterac.dispose;
      clearHoverHighlight = clearHover;
      disposeSegmentInteractions = disposeSegmentInteractionsFromCreate;
      const clickHandle = view.on('click', handleMapClick);
      const pointerMoveHandle = view.on('pointer-move', handlePointerMove);
      const closePopoverOnNavigate = () => {
        setSelectedFeature(null);
        setPopoverPoint(null);
      };
      const navigatingHandle = view.watch('navigating', navigating => {
        if (navigating) closePopoverOnNavigate();
      });
      eventHandlesRef.current = [clickHandle, pointerMoveHandle, navigatingHandle];
    });

    // Cleanup function (only call segment interaction teardown if view.when() ran)
    const cleanup = () => {
      if (basemapToggleWidget) {
        basemapToggleWidget.destroy();
        basemapToggleWidget = null;
      }
      if (typeof disposeSegmentInteractions === 'function') {
        disposeSegmentInteractions();
      }
      eventHandlesRef.current.forEach(handle => {
        if (handle && handle.remove) {
          handle.remove();
        }
      });
      eventHandlesRef.current = [];
      if (typeof clearHoverHighlight === 'function') {
        clearHoverHighlight();
      }
      if (mapViewRef.current) {
        mapViewRef.current.container = null;
        mapViewRef.current.destroy();
      }
    };
    // eslint-disable-next-line consistent-return
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cabinetIdentifier]);
  useEffect(() => {
    const view = mapViewRef.current;
    if (!view || !view.map) return () => {};
    let cancelled = false;
    const kmlUrl = extractKMLUrl(kml);

    // Cleanup previous layers
    if (centerlineLayerRef.current) {
      view.map.remove(centerlineLayerRef.current);
      centerlineLayerRef.current = null;
    }
    if (activityLayersRef.current._group) {
      view.map.remove(activityLayersRef.current._group);
    }
    activityLayersRef.current = {};
    centerlineFeatureRef.current = null;
    if (!kmlUrl) {
      return () => {
        cancelled = true;
      };
    }

    // Parse KML URL to GeoJSON and detect centerline
    const detectCenterline = async () => {
      try {
        const geojson = await parseKMLToGeoJSON(kmlUrl);
        if (cancelled) return;

        // Find LineString features
        const lineStrings = findLineStringFeatures(geojson);
        if (lineStrings.length === 0) {
          if (!cancelled) {
            setCenterlineData({
              startPoint: null,
              endPoint: null,
              geometry: null
            });
          }
          return;
        }

        // Select the longest LineString as the centerline
        const centerlineFeature = selectLongestLineString(lineStrings);
        if (!centerlineFeature || cancelled) return;

        // Store centerline feature for segment rendering
        centerlineFeatureRef.current = centerlineFeature;

        // Extract start and end points
        const endpoints = extractEndpoints(centerlineFeature);
        if (endpoints) {
          if (!cancelled) {
            setCenterlineData({
              startPoint: endpoints.startPoint,
              endPoint: endpoints.endPoint,
              geometry: centerlineFeature.geometry
            });
            endpointsRef.current = endpoints;
          }
        }
        if (cancelled) return;

        // Create a graphics layer for the centerline overlay
        const centerlineLayer = new GraphicsLayer({
          title: 'Pipeline Centerline'
        });

        // Convert GeoJSON to ArcGIS paths format
        const paths = geojsonToArcGISPaths(centerlineFeature);

        // Create the centerline graphic with distinct styling
        const centerlineOverlay = new Graphic({
          geometry: {
            type: 'polyline',
            paths,
            spatialReference: {
              wkid: 4326
            } // WGS84
          },
          symbol: {
            type: 'simple-line',
            color: [255, 0, 0, 1],
            // Red
            width: 1,
            // Thin line to signify centerline
            style: 'solid'
          }
        });
        centerlineLayer.add(centerlineOverlay);

        // Wait for view to be ready, then add layer on top (guard in case we unmounted)
        view.when(() => {
          if (cancelled || !(view !== null && view !== void 0 && view.map)) return;
          view.map.add(centerlineLayer);
          centerlineLayerRef.current = centerlineLayer;
          if (typeof view.goTo === 'function') {
            Promise.resolve().then(() => view.goTo(centerlineOverlay)).catch(() => {});
          }
        });
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('Failed to parse KML for centerline detection:', error);
        }
      }
    };

    // Start centerline detection (KML URL fetch + GeoJSON; no Esri KMLLayer)
    detectCenterline();

    // Cleanup function
    return () => {
      cancelled = true;
      if (centerlineLayerRef.current && view.map) {
        view.map.remove(centerlineLayerRef.current);
        centerlineLayerRef.current = null;
      }
      if (activityLayersRef.current._group && view.map) {
        view.map.remove(activityLayersRef.current._group);
      }
      activityLayersRef.current = {};
    };
  }, [kml]);

  // Effect to render activity segments when activities or centerline changes
  // centerlineData.geometry triggers re-run; centerlineFeatureRef holds full GeoJSON
  useEffect(() => {
    const view = mapViewRef.current;
    const centerlineFeature = centerlineFeatureRef.current;
    if (!view || !view.map || !centerlineData.geometry || !centerlineFeature) return;

    // Cleanup previous activity layers (remove group layer which contains all)
    if (activityLayersRef.current._group) {
      view.map.remove(activityLayersRef.current._group);
    }
    activityLayersRef.current = {};
    if (!activities || activities.length === 0) return;
    const totalLength = getLineLength(centerlineFeature);

    // Create layers per activity type (so each can be toggled in LayerList)
    const layersByType = {};
    activities.forEach(activity => {
      const fromDistance = activity.fromDistance,
        toDistance = activity.toDistance,
        activityType = activity.activityType;

      // Validate distances and bounds
      if (fromDistance == null || toDistance == null) return;
      if (fromDistance < 0 || toDistance < 0) return;
      if (fromDistance > totalLength || toDistance > totalLength) return;
      if (fromDistance >= toDistance) return;
      const resolvedActivityType = activityType !== null && activityType !== void 0 ? activityType : DEFAULT_ACTIVITY_TYPE;
      const segmentFeature = getLineSlice(centerlineFeature, fromDistance, toDistance);
      if (!(segmentFeature !== null && segmentFeature !== void 0 && segmentFeature.geometry)) return;

      // Convert GeoJSON to ArcGIS paths format
      const paths = geojsonToArcGISPaths(segmentFeature);

      // Get color and title from legend items (case-insensitive match)
      const activityColor = getActivityColor(resolvedActivityType, legend);
      const legendItem = legend.find(item => {
        var _item$identifier2;
        return item.identifier === resolvedActivityType || ((_item$identifier2 = item.identifier) === null || _item$identifier2 === void 0 ? void 0 : _item$identifier2.toLowerCase()) === (resolvedActivityType === null || resolvedActivityType === void 0 ? void 0 : resolvedActivityType.toLowerCase());
      });
      const layerTitle = (legendItem === null || legendItem === void 0 ? void 0 : legendItem.title) || resolvedActivityType;

      // Get or create layer for this activity type
      if (!layersByType[resolvedActivityType]) {
        layersByType[resolvedActivityType] = new GraphicsLayer({
          title: layerTitle
        });
      }
      const segmentGraphic = new Graphic({
        geometry: {
          type: 'polyline',
          paths,
          spatialReference: {
            wkid: 4326
          } // WGS84
        },
        symbol: {
          type: 'simple-line',
          color: activityColor,
          width: 4,
          style: 'solid'
        },
        attributes: buildSegmentAttributes(activity, cabinetIdentifier, DEFAULT_ACTIVITY_TYPE)
      });
      layersByType[resolvedActivityType].add(segmentGraphic);
    });

    // Add all activity layers to the map inside a GroupLayer
    const layerTypes = Object.keys(layersByType);
    if (layerTypes.length > 0) {
      view.when(() => {
        if (!(view !== null && view !== void 0 && view.map)) return;
        const workProgressGroup = new GroupLayer({
          title: 'Work Progress',
          layers: layerTypes.map(type => layersByType[type])
        });
        view.map.add(workProgressGroup);
        activityLayersRef.current = _objectSpread({
          _group: workProgressGroup
        }, layersByType);
      });
    }
  }, [activities, centerlineData, legend, cabinetIdentifier]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: mapWrapperRef,
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "arcgis-map-container",
    style: {
      width: '100%',
      minHeight: isFullscreen ? '100vh' : '400px',
      height: isFullscreen ? '100vh' : '50vh',
      maxHeight: isFullscreen ? 'none' : '800px',
      position: 'relative',
      background: isFullscreen ? '#000' : 'transparent'
    },
    ref: mapContainerRef
  }), /*#__PURE__*/React.createElement("div", {
    ref: popoverTargetRef,
    style: {
      position: 'absolute',
      left: "".concat((_popoverPoint$x = popoverPoint === null || popoverPoint === void 0 ? void 0 : popoverPoint.x) !== null && _popoverPoint$x !== void 0 ? _popoverPoint$x : -9999, "px"),
      top: "".concat((_popoverPoint$y = popoverPoint === null || popoverPoint === void 0 ? void 0 : popoverPoint.y) !== null && _popoverPoint$y !== void 0 ? _popoverPoint$y : -9999, "px"),
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
      zIndex: 20
    }
  }), /*#__PURE__*/React.createElement(FeaturePopover, {
    isOpen: Boolean(selectedFeature),
    target: popoverTargetRef.current,
    container: mapWrapperRef.current,
    feature: selectedFeature,
    onClose: () => {
      setSelectedFeature(null);
      setPopoverPoint(null);
    }
  })), legendContainer && /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement("div", {
    style: {
      background: 'white',
      padding: '10px 12px',
      fontFamily: '"Avenir Next", "Helvetica Neue", sans-serif',
      fontSize: '12px',
      minWidth: '140px',
      maxHeight: '300px',
      overflowY: 'auto'
    }
  }, legend.length === 0 ? /*#__PURE__*/React.createElement("div", {
    style: {
      color: '#6e6e6e'
    }
  }, "No items") : /*#__PURE__*/React.createElement(React.Fragment, null, legend.map(_ref3 => {
    let identifier = _ref3.identifier,
      label = _ref3.title,
      color = _ref3.color,
      _ref3$style = _ref3.style,
      style = _ref3$style === void 0 ? 'line' : _ref3$style;
    return /*#__PURE__*/React.createElement("div", {
      key: identifier,
      style: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '6px'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: '20px',
        height: style === 'line' ? '4px' : '12px',
        background: color,
        marginRight: '8px',
        borderRadius: '2px'
      }
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        color: '#323232'
      }
    }, label));
  }))), legendContainer));
};
ArcGisMap.propTypes = {
  login: PropTypes.shape({
    user: PropTypes.shape({
      arcGisApiKey: PropTypes.string
    })
  }),
  kml: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    remotePath: PropTypes.shape({
      _cache: PropTypes.string
    })
  }), PropTypes.object]),
  activities: PropTypes.arrayOf(PropTypes.shape({
    fromDistance: PropTypes.number,
    toDistance: PropTypes.number,
    activityType: PropTypes.string,
    formID: PropTypes.string,
    formTypeIdentifier: PropTypes.string,
    cabinetIdentifier: PropTypes.string,
    displayFields: PropTypes.arrayOf(PropTypes.shape({
      identifier: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string
    }))
  })),
  cabinetIdentifier: PropTypes.string,
  legend: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    style: PropTypes.oneOf(['line', 'swatch'])
  }))
};
export { ArcGisMap as UncomposedArcGisMap };
export default entityComposition(ArcGisMap, ['login'], {}, []);